import axios from "axios";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {
  Box,
  Button,
  Grid2,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";

const ITEM_HEIGHT = 40;
const ITEM_PADDING_TOP = 0;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 150,
      background: "#EBF3FD",
    },
  },
};

const Step4 = ({
  sra,
  setSra,
  setStep,
  approvers,
  setApprovers,
  noOfApprovers,
  setNoOfApprovers,
  storedUser,
}) => {
  const [btnLoading, setBtnLoading] = useState(false);
  const [orgUsers, setOrgUsers] = useState([]);
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();

  const cfoUsers = orgUsers.filter((u) => u.userRoleName === "CFO" && u.isEnable);

  const buildApproversArray = (current, users, count) => {
    const cfos = users.filter((u) => u.userRoleName === "CFO" && u.isEnable);
    const cfoEmails = new Set(cfos.map((u) => u.email));

    const resized = Array.from(
      { length: count },
      (_, i) => {
        const val = current[i] || "";
        return i < count - 1 && cfoEmails.has(val) ? "" : val;
      },
    );

    if (cfos.length === 1) resized[count - 1] = cfos[0].email;
    else if (!resized[count - 1]) resized[count - 1] = ""; // leave empty if multiple CFOs

    return resized;
  };

  useEffect(() => {
    const getOrgUsers = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/org-user/all?username=${storedUser.username}`,
          { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
        );
        setOrgUsers(res.data);
      } catch (error) {
        console.log(error);
      }
    };
    getOrgUsers();
  }, []);

  useEffect(() => {
    setApprovers((prev) => buildApproversArray(prev, orgUsers, noOfApprovers));
  }, [noOfApprovers]);

  useEffect(() => {
    if (noOfApprovers === 0) return;
    setApprovers((prev) => buildApproversArray(prev, orgUsers, noOfApprovers));
  }, [orgUsers]);

  useEffect(() => {
    const fetchDraft = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/draft-vendor-advance/approver?draftSraId=${sra.draftVendorAdvanceId}`,
          { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
        );
        const responseApprovers = res.data
          .sort((a, b) => a.number - b.number)
          .map((item) => item.approver?.email || "");
        setApprovers(responseApprovers);
      } catch (error) {
        console.error("Failed to fetch draft:", error);
      }
    };
    fetchDraft();
  }, []);

  const getSlotOptions = (index) => {
    const isLastSlot = index === noOfApprovers - 1;

    if (isLastSlot) {
      return cfoUsers.filter(
        (u) => !approvers.some((email, i) => i !== index && email === u.email),
      );
    }

    return orgUsers.filter(
      (user) =>
        !["BUYER", "FINANCE_PROCESSOR", "SUPERUSER", "ACCOUNT_PAYABLE_TEAM", "CFO"].includes(
          user.userRoleName,
        ) &&
        !approvers.some((email, i) => i !== index && email === user.email) &&
        user.isEnable,
    );
  };

  const validateForm = () => {
    const newErrors = {};

    if (noOfApprovers === 0) {
      newErrors.noOfApprovers = "Please select at least one approver.";
    }

    approvers.slice(0, noOfApprovers).forEach((approver) => {
      if (!approver || approver.trim?.() === "") {
        newErrors.approver = "Please select all approvers as per the selected number.";
      }
    });

    if (!newErrors.approver && noOfApprovers > 0) {
      const lastUser = orgUsers.find((u) => u.email === approvers[noOfApprovers - 1]);
      if (!lastUser || lastUser.userRoleName !== "CFO") {
        newErrors.approver = "The last approver must be a CFO.";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleApproversChange = (index, value) => {
    const updated = [...approvers];
    updated[index] = value;
    setApprovers(updated);
  };

  const handleNext = async () => {
    if (!validateForm()) return;
    setBtnLoading(true);
    try {
      const indexedData = approvers.map((item, index) => ({
        ...item,
        username: item,
        number: index + 1,
        numberOfApprovers: noOfApprovers,
      }));
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/draft-vendor-advance/approver?draftSraId=${sra.draftVendorAdvanceId}`,
        indexedData,
        { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
      );
      setStep((prev) => prev + 1);
    } catch (error) {
      console.log(error);
    } finally {
      setBtnLoading(false);
    }
  };

  const handleBack = () => setStep((prev) => prev - 1);

  const isLastSlotLocked = noOfApprovers > 0 && cfoUsers.length === 1;

  return (
    <div style={{ position: "relative" }}>
      {btnLoading && <div className="loading-div"></div>}
      <h2>Approval Authority Selection</h2>
      <div className="progress-container">
        <span className="blue-bar" style={{ width: "80%" }}></span>
      </div>

      <div className="dashboard-container">
        <Typography className="form-title">Select Approvers</Typography>
        <label style={{ fontWeight: "bold" }}>Number of Approvers:</label>
        <select
          className={`${errors.noOfApprovers && "input-error"}`}
          value={noOfApprovers}
          onChange={(e) => {
            setNoOfApprovers(Number(e.target.value));
            setErrors({ ...errors, noOfApprovers: "" });
          }}
          style={{ marginLeft: "10px", padding: "5px", width: "200px" }}
        >
          <option value="">Select</option>
          {[1, 2, 3, 4].map((num) => (
            <option key={num} value={num}>
              {num}
            </option>
          ))}
        </select>
        {errors.noOfApprovers && (
          <p className="error">{errors.noOfApprovers}</p>
        )}
        <Grid2 container spacing={2}>
          {Array.from({ length: noOfApprovers }, (_, index) => {
            const isLastSlot = index === noOfApprovers - 1;
            const locked = isLastSlotLocked && isLastSlot;
            const slotOptions = getSlotOptions(index);

            return (
              <Grid2 key={index} size={{ xs: 12, sm: 4 }}>
                <label>
                  Approver {index + 1}
                  {isLastSlot && (
                    <span style={{ color: "#EA981F", marginLeft: 4 }}>
                      (CFO required)
                    </span>
                  )}
                  :
                </label>
                <Select
                  value={approvers[index] || ""}
                  onChange={(e) => handleApproversChange(index, e.target.value)}
                  displayEmpty
                  disabled={locked}
                  IconComponent={(props) => (
                    <KeyboardArrowDownIcon {...props} fontSize="small" />
                  )}
                  MenuProps={MenuProps}
                  sx={{
                    display: "flex",
                    borderRadius: "7px",
                    padding: "8px 7px",
                    fontSize: "14px",
                    margin: "7px 0 20px 0",
                    opacity: locked ? 0.7 : 1,
                    "& .MuiSelect-select": {
                      fontFamily: "Inter !important",
                      padding: "5px",
                      "&:hover": { cursor: locked ? "not-allowed" : "default" },
                    },
                  }}
                >
                  <MenuItem value="" disabled>
                    {isLastSlot ? "Select CFO" : "Select Approver"}
                  </MenuItem>
                  {slotOptions.map((user) => (
                    <MenuItem key={user.email} value={user.email}>
                      {user.name} ({user.userRoleName.replace(/_/g, " ")})
                    </MenuItem>
                  ))}
                </Select>
              </Grid2>
            );
          })}
        </Grid2>
        {errors.approver && <p className="error">{errors.approver}</p>}
      </div>

      <Box className="btn-container" sx={{ gap: 3 }}>
        <Button className="btn-outlined" onClick={handleBack}>
          Back
        </Button>
        <Button
          className="btn-contained"
          onClick={handleNext}
          disabled={btnLoading}
        >
          {btnLoading ? "Submitting..." : "Next"}
        </Button>
      </Box>
    </div>
  );
};

export default Step4;
